import { useState, useEffect } from 'react';
import packageJson from '../package.json';
import { db } from './db';

// Records the version we last auto-reloaded for. If we come back up still mismatched
// (a stubbornly cached index.html, say) we hand off to the app rather than bouncing
// the browser in a loop on a screen the user cannot get past.
const RELOAD_GUARD_KEY = 'cacheBusterReloadedFor';

const readGuard = () => {
  try {
    return sessionStorage.getItem(RELOAD_GUARD_KEY);
  } catch {
    return null; // private mode / storage disabled
  }
};

const writeGuard = (version) => {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, version);
  } catch { /* private mode / storage disabled */ }
};

// Forward-only on purpose. This reload is automatic and runs before login, so a
// rollback must never be able to bounce the browser between two versions. The
// "Update available" button covers rollbacks, with the user in control.
const semverGreaterThan = (versionA, versionB) => {
  const versionsA = versionA.split(/\./g);
  const versionsB = versionB.split(/\./g);
  while (versionsA.length || versionsB.length) {
    const a = Number(versionsA.shift());
    const b = Number(versionsB.shift());
    if (a === b) continue;
    return a > b || isNaN(b);
  }
  return false;
};

const CacheBuster = ({ children }) => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    // Never leave the app stuck behind this check: any failure renders the app as-is.
    const proceed = () => { if (!cancelled) setLoading(false); };

    const reloadFor = (latestVersion) => {
      console.info(`We have a new version - ${latestVersion}. Reloading.`);
      writeGuard(latestVersion);
      // Drop the offline cache so the new bundle does not read stale rows, then reload.
      // The session itself is left alone - a version bump is not a reason to log anyone out.
      Promise.resolve(db.cache.clear())
        .catch((err) => console.warn('CacheBuster: cache clear failed', err))
        .finally(() => window.location.reload());
    };

    const check = async () => {
      try {
        const response = await fetch(`/meta.json?_=${Date.now()}`, { cache: 'no-store' });
        if (!response.ok) return proceed();
        // A missing meta.json does not 404 here - the SPA fallback answers with index.html
        // and a 200 - so check the type rather than letting the parse error fall through to
        // the catch, which looks identical to a network blip.
        const type = response.headers.get('content-type') || '';
        if (!type.includes('json')) {
          console.warn(`meta.json did not return JSON (${type || 'no content-type'}). Skipping the version check - did this build run generate-build-version?`);
          return proceed();
        }
        const meta = await response.json();
        if (cancelled) return;

        const latestVersion = meta.version;
        const currentVersion = packageJson.version;
        if (!semverGreaterThan(latestVersion, currentVersion)) {
          console.info(`You already have the latest version - ${latestVersion}. No cache refresh needed.`);
          return proceed();
        }
        if (readGuard() === latestVersion) {
          console.warn(`Still on ${currentVersion} after reloading for ${latestVersion}. Continuing without another reload.`);
          return proceed();
        }
        reloadFor(latestVersion);
      } catch (err) {
        console.warn('CacheBuster: version check failed, continuing', err);
        proceed();
      }
    };

    check();
    return () => { cancelled = true; };
  }, []);

  return children({ loading });
};

export default CacheBuster;
